/**
 * Forecasting Hub — tabbed interface for all forecasting features.
 * Uses URL-based tab routing for deep-linkable tabs.
 * Shared filter bar (Brand, Category, City, Store, Period) above tabs.
 *
 * Keep-alive rendering: ALL tabs stay mounted (hidden via display:none)
 * so that filter state and loaded data persist across tab switches.
 */
import React, { useMemo } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ManualOverrideTab from './ManualOverrideTab';
import NewProductForecastTab from './NewProductForecastTab';
import CannibalizationTab from './CannibalizationTab';
import YearOverYearTab from './YearOverYearTab';
import SeasonalProfilesTab from './SeasonalProfilesTab';
import ProjectionGridTab from './ProjectionGridTab';
import ForecastingSettingsTab from './ForecastingSettingsTab';
import { ForecastingFilterProvider } from './ForecastingFilterContext';
import ForecastingFilterBar from './ForecastingFilterBar';

const tabs = [
  { label: 'SKU Projection', path: '/forecasting/sku-projection', Component: ProjectionGridTab },
  { label: 'Manual Override', path: '/forecasting/manual-override', Component: ManualOverrideTab },
  { label: 'New Product Forecast', path: '/forecasting/new-product-forecast', Component: NewProductForecastTab },
  { label: 'Cannibalization', path: '/forecasting/cannibalization', Component: CannibalizationTab },
  { label: 'Year-over-Year', path: '/forecasting/year-over-year', Component: YearOverYearTab },
  { label: 'Seasonal Profiles', path: '/forecasting/seasonal-profiles', Component: SeasonalProfilesTab },
  { label: 'Settings', path: '/forecasting/settings', Component: ForecastingSettingsTab },
];

const ForecastingHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTabIndex = useMemo(() => {
    const idx = tabs.findIndex((t) => location.pathname === t.path);
    return idx >= 0 ? idx : 0;
  }, [location.pathname]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(tabs[newValue].path);
  };

  return (
    <ForecastingFilterProvider>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Fixed header: title + filter + tabs (do not scroll) */}
        <Box sx={{ flexShrink: 0 }}>
          {/* Page title */}
          <Box sx={{ mb: '20px' }}>
            <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
              Forecasting
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
              Projections, manual overrides, new product forecasts, and cannibalization analysis
            </Typography>
          </Box>

          {/* Shared filter bar */}
          <ForecastingFilterBar />

          {/* Tab navigation */}
          <Box sx={{ borderBottom: '1px solid var(--color-border)', mb: 3 }}>
            <Tabs
              value={activeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab) => (
                <Tab key={tab.path} label={tab.label} />
              ))}
            </Tabs>
          </Box>
        </Box>

        {/* Scrollable tab content area — only this scrolls */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {/* Keep-alive tab content — all tabs stay mounted, inactive ones hidden */}
          {tabs.map((tab, idx) => {
            const TabComponent = tab.Component;
            return (
              <Box
                key={tab.path}
                sx={{ display: activeTabIndex === idx ? 'block' : 'none' }}
              >
                <TabComponent />
              </Box>
            );
          })}
        </Box>
      </Box>
    </ForecastingFilterProvider>
  );
};

export default ForecastingHub;
